import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { Layout } from './Layout';

/**
 * Client side route guard.
 *
 * This only prevents the internal UI from rendering for a visitor without a
 * session - it is convenience, not security. Every piece of data behind it comes
 * from the API, which performs its own server side check on each request.
 */
export function ProtectedRoute({ children }: { children: ReactNode }): ReactNode {
  const { session } = useSession();
  const location = useLocation();

  if (session === undefined) {
    return (
      <Layout>
        <div className="card card--centered" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <p>Sitzung wird geprueft ...</p>
        </div>
      </Layout>
    );
  }

  if (session.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (session.status === 'denied') {
    return <Navigate to="/zugriff-verweigert" replace />;
  }

  if (session.status === 'error') {
    return (
      <Layout>
        <div className="card">
          <h1>Die Anwendung ist derzeit nicht verfuegbar</h1>
          <p>{session.message}</p>
          {session.correlationId ? (
            <p className="muted">
              Referenz fuer den Support: <code>{session.correlationId}</code>
            </p>
          ) : null}
        </div>
      </Layout>
    );
  }

  return children;
}
